import React, { useState, useEffect } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export default function RiskAssessmentsModal({ serverId, serviceUserId, onClose }) {
    const [risks, setRisks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchRisks = async () => {
            setLoading(true);
            try {
                const ref = collection(db, 'servers', serverId, 'serviceUsers', serviceUserId, 'riskAssessment');
                const snap = await getDocs(query(ref));
                
                const loaded = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                loaded.sort((a, b) => {
                    const getMs = (val) => {
                        if (!val) return 0;
                        if (typeof val.toDate === 'function') return val.toDate().getTime();
                        if (val.seconds) return val.seconds * 1000;
                        return new Date(val).getTime() || 0;
                    };
                    const dateA = getMs(a.createdAt || a.date);
                    const dateB = getMs(b.createdAt || b.date);
                    return dateB - dateA;
                });
                
                setRisks(loaded);
            } catch (err) {
                console.error("Error fetching risk assessments:", err);
                setError("Failed to load risk assessments.");
            } finally {
                setLoading(false);
            }
        };

        if (serverId && serviceUserId) {
            fetchRisks();
        }
    }, [serverId, serviceUserId]);

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '20px', color: '#111827' }}>Risk Assessments</h2>
                        <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>Active and historical risk assessments</p>
                    </div>
                    <button onClick={onClose} style={styles.closeBtn}>×</button>
                </div>

                <div style={styles.scrollArea}>
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading risk assessments...</div>
                    ) : error ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>{error}</div>
                    ) : risks.length === 0 ? (
                        <div style={styles.emptyState}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                            <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No Risk Assessments</h3>
                            <p style={{ margin: 0, color: '#6b7280' }}>There are no risk assessments currently associated with this service user.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {risks.map((risk, index) => {
                                const title = risk.title || risk.riskType || risk.name || 'Untitled Risk Assessment';
                                const isCurrent = risk.isCurrent !== false && risk.status !== 'archived';
                                
                                return (
                                    <div key={risk.id || index} style={styles.recordCard}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                            <div>
                                                <h3 style={{ margin: 0, fontSize: '16px', color: '#111827' }}>{title}</h3>
                                                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                                                    Created by: {risk.createdBy || risk.author || 'System'}
                                                </div>
                                            </div>
                                            <span style={{ 
                                                display: 'inline-block', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 'bold',
                                                backgroundColor: isCurrent ? '#fef3c7' : '#f3f4f6', 
                                                color: isCurrent ? '#92400e' : '#6b7280', 
                                                border: `1px solid ${isCurrent ? '#fde68a' : '#d1d5db'}`
                                            }}>
                                                {isCurrent ? 'ACTIVE' : 'ARCHIVED'}
                                            </span>
                                        </div>
                                        
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>
                                            <div style={{ flex: '1', minWidth: '150px' }}>
                                                <label style={styles.fieldLabel}>Created Date</label>
                                                <div style={styles.fieldValue}>{risk.createdAt ? new Date(risk.createdAt?.seconds ? risk.createdAt.seconds * 1000 : risk.createdAt).toLocaleDateString() : 'N/A'}</div>
                                            </div>
                                            <div style={{ flex: '1', minWidth: '150px' }}>
                                                <label style={styles.fieldLabel}>Review Date</label>
                                                <div style={styles.fieldValue}>{risk.reviewDate ? new Date(risk.reviewDate?.seconds ? risk.reviewDate.seconds * 1000 : risk.reviewDate).toLocaleDateString() : 'Not Set'}</div>
                                            </div>
                                        </div>
                                        
                                        <div style={{ marginTop: '16px' }}>
                                            <button style={styles.viewBtn} onClick={() => alert('View detailed Risk Assessment form: Coming to React V2 soon.')}>
                                                Open Risk Assessment
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '700px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
    modalHeader: { padding: '24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    closeBtn: { background: 'none', border: 'none', fontSize: '24px', color: '#9ca3af', cursor: 'pointer', lineHeight: 1 },
    scrollArea: { padding: '24px', overflowY: 'auto', flex: 1, backgroundColor: '#f9fafb' },
    emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0', textAlign: 'center', backgroundColor: 'white', borderRadius: '8px', border: '1px dashed #d1d5db' },
    recordCard: { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
    fieldLabel: { display: 'block', fontSize: '11px', textTransform: 'uppercase', color: '#6b7280', marginBottom: '4px', fontWeight: 'bold' },
    fieldValue: { fontSize: '14px', color: '#111827' },
    viewBtn: { width: '100%', padding: '10px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', color: '#374151', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }
};
